import React, { useCallback, forwardRef } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import split from 'lodash/split'
import toLower from 'lodash/toLower'

import userProfileSchema from '@galo-monitor/forms/schemas/common/userProfile.schema'
import { useT } from '@galo-monitor/i18n'
import { useSnackbar } from '@galo-monitor/snackbar'
import { LanguageActions } from '@galo-monitor/stores/language'
import { UserActions } from '@galo-monitor/stores/user'
import Form from '@galo-monitor/web-components/Form'
import InputEmail from '@galo-monitor/web-components/InputEmail'
import InputPhone from '@galo-monitor/web-components/InputPhone'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputText from '@galo-monitor/web-components/InputText'

import { Container } from './theme'

const UserProfileForm = forwardRef((props, formRef) => {
  const {
    user,
    languageOptions,
    loading,
    setLoading,
    onSuccess,
    onError
  } = props
  const dispatch = useCallback(useDispatch(), [])
  const t = useT()
  const snackbar = useSnackbar()

  const handleSubmit = useCallback(
    data => {
      setLoading(true)
      dispatch(UserActions.savePersonalInformation(
        data,
        () => {
          snackbar.success(t('{this} {action} successfully!', {
            this: toLower(t('personal information', { howMany: 1 })),
            action: toLower(t('updated', { gender: 'female', howMany: 1 }))
          }))
          dispatch(LanguageActions.changeLanguage(split(data.language, '-')[0]))
          onSuccess()
        },
        onError
      ))
    },
    [setLoading, dispatch, onError, snackbar, t, onSuccess]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ userProfileSchema }
      onSubmit={ handleSubmit }
    >
      <Container>
        <InputText
          name="name"
          label={ t('name') }
          defaultValue={ user.name }
          disabled={ loading }
          fullWidth
        />

        <InputEmail
          name="email"
          label={ t('email') }
          defaultValue={ user.email }
          disabled
          fullWidth
        />

        <InputPhone
          name="phone"
          label={ t('phone') }
          defaultValue={ user.phone ?? '' }
          disabled={ loading }
          fullWidth
        />

        <InputSelect
          name="language"
          label={ t('language') }
          defaultValue={ user.language || '' }
          options={ languageOptions }
          disabled={ loading }
          fullWidth
        />
      </Container>
    </Form>
  )
})

UserProfileForm.propTypes = {
  user: PropTypes.object,
  languageOptions: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  setLoading: PropTypes.func,
  onSuccess: PropTypes.func,
  onError: PropTypes.func
}

UserProfileForm.defaultProps = {
  user: {},
  loading: false,
  setLoading: () => {},
  onSuccess: () => {},
  onError: () => {}
}

export default UserProfileForm
